module OnlineDPR.Application.Client.Components

open System

open Fable.Core
open Fable.React
open Fable.React.Props

open OnlineDPR.Application.Shared
open OnlineDPR.Application.Shared.DateHandling
open OnlineDPR.Application.Shared.Translations

type FieldComponentProps = { field: Field; fieldChanged: Field -> unit }
type ReadOnlyFieldComponentProps = { value: string; key: string; labelTranslationKey: TranslationKey option; postfix: string option }
type ReadOnlyTableFieldComponentProps = { value: string; key: string; postfix: string option }

[<Emit("bindItemList()")>]
let bindItemList(): unit = jsNative

let maybeWithError valmessage htmlClasses =
    match valmessage with
    | Some _ -> htmlClasses + " has-error"
    | None -> htmlClasses

let validationMessage e =
    String.Format(CurrentTranslations.Translations.[e.MessageKey], e.Params)

let valueProp value readonly =
    match readonly with
    | false -> DefaultValue value :> IHTMLProp
    | true -> Value value :> IHTMLProp

let getLabelText field  =
    match field.HideLabel with
    | false -> match field.LabelTranslationKey with
               | Some key -> Some CurrentTranslations.Translations.[key]
               | None -> match field.Label with
                         | Some s -> Some s
                         | None -> Some field.Key
    | true -> None

let formEventHandler onChange =
    match onChange with
            | Some e -> e
            | None -> fun _ -> ()

let getInput isReadOnly key value classes onChange (props:IHTMLProp list) =
    let initialProps =
          [  (Disabled isReadOnly :> IHTMLProp)
             (valueProp value isReadOnly)
             (Key key :> IHTMLProp)
             (Class ("form-control " + classes) :> IHTMLProp)
             (OnChange (formEventHandler onChange) :> IHTMLProp) ]
    let props = initialProps
                |> List.append props
    input props



let controlForField field onChange =
    match field.FieldType with
    | FieldType.String ->
        getInput field.IsReadOnly field.Key field.Value "" onChange [ (HTMLAttr.Type "text" :> IHTMLProp) ]
    | FieldType.Int
    | FieldType.Decimal ->
        getInput field.IsReadOnly field.Key field.Value "number" onChange [ (HTMLAttr.Type "text" :> IHTMLProp) ]
    | FieldType.SelectOne o ->
        select [ Class "form-control"
                 OnChange (formEventHandler onChange)
                 DefaultValue field.Value ]
               (o.Items |> List.map (fun i -> option [ Value i.Value] [ str i.DisplayName ]))
    | FieldType.Date o ->
        getInput field.IsReadOnly field.Key field.Value "" onChange [ (HTMLAttr.Type "date" :> IHTMLProp);
                                                                      ((HTMLAttr.Min (DayDateOptionToJsString o.Min)) :> IHTMLProp);
                                                                      ((HTMLAttr.Max (DayDateOptionToJsString o.Max)) :> IHTMLProp) ]

let inline formgroup field onChange hideValidationMessage =
    let labeltext = getLabelText field
    div [ Class (maybeWithError field.ValidationMessage "form-group row")]
        [   match labeltext with
            | Some s -> yield label [ Class (sprintf "control-label col-form-label col-sm-3 label-%s" field.Key) ] [ str s ]
            | None -> ()
            yield div [ Class "col" ]
                      [ yield div [ Class "input-group" ]
                                  [ yield controlForField field onChange
                                    match field.Postfix with
                                    | Some p -> yield div [ Class "input-group-append" ] [ span [ ClassName "input-group-text" ] [str p] ]
                                    | None -> () ]
                        match field.ValidationMessage, hideValidationMessage with
                        | Some msg, false -> yield span [ ClassName "has-error"] [ str (validationMessage msg) ]
                        | Some _, true
                        | None, _ -> () ]]

let fieldComponent { field = field; fieldChanged = fieldChanged } =
    let onChange = fun (ev: Browser.Types.Event) -> fieldChanged { field with Value = ev.Value }

    formgroup field (Some onChange) false

let tableFieldComponent { field = field; fieldChanged = fieldChanged } =
    let onChange = fun (ev: Browser.Types.Event) -> fieldChanged { field with Value = ev.Value }
    formgroup field (Some onChange) false

let inline TableFieldComponent field fieldChanged =
    let field = { field with HideLabel = true }
    (ofFunction tableFieldComponent) { field = field; fieldChanged = fieldChanged } []

let readOnlyField key value translationKey postfix =
    Field.Init key FieldType.String (fun f -> {f with IsReadOnly = true
                                                      FieldType = FieldType.Decimal
                                                      LabelTranslationKey = translationKey
                                                      Value = value
                                                      Postfix = postfix })
let readOnlyFieldComponent { value = value; key = key; labelTranslationKey = labelTranslationKey; postfix = postfix} =
    formgroup (readOnlyField key value labelTranslationKey postfix) None false

let readOnlyTableFieldComponent ({ value = value; key = key; postfix = postfix }:ReadOnlyTableFieldComponentProps) =
    formgroup (readOnlyField key value None postfix) None false

let inline FieldComponent field fieldChanged =
    (ofFunction fieldComponent) { field = field; fieldChanged = fieldChanged } []

let inline ReadOnlyFieldComponent value key labelTranslationKey postfix =
    (ofFunction readOnlyFieldComponent) { value = value; key = key; labelTranslationKey = labelTranslationKey; postfix = postfix } []

let inline ReadOnlyTableFieldComponent value key postfix=
    (ofFunction readOnlyTableFieldComponent) { value = value; key = key; postfix = postfix } []

let inlineFormFieldComponent { field = field; fieldChanged = fieldChanged } =
    let onChange = fun (ev:Browser.Types.Event) -> fieldChanged { field with Value = ev.Value }
    formgroup field (Some onChange) true

let inline InlineFormFieldComponent field fieldChanged =
    (ofFunction inlineFormFieldComponent) { field = field; fieldChanged = fieldChanged } []


let (|NotNullOrWhitespace|NullOrWhitespace|) str =
    match str with
    |str when System.String.IsNullOrWhiteSpace str -> NullOrWhitespace
    |_ -> NotNullOrWhitespace

let getDecimal (s:string) =
    let s = s.Replace(".", "___") //forbid dots
             .Replace(',', '.')
    match Decimal.TryParse(s) with
    | true, v -> Some v
    | false, _ -> None

let isDecimal (s:string) =
    match getDecimal s with
    | Some _ -> true
    | None -> false

let getDecimalString(d:decimal)=
    (sprintf "%.2f" d).Replace('.',',')

let getDecimalStringWithCurrency(d:decimal) c =
    sprintf "%s %s" (getDecimalString d) c

let maxDateOrMax (d:DayDate option)=
    match d with
    | Some d -> new DateTime(d.Year, d.Month, d.Day, 23, 59, 59)
    | None -> DateTime.MaxValue

let minDateOrMin (d:DayDate option) =
    match d with
    | Some d -> new DateTime(d.Year, d.Month, d.Day)
    | None -> DateTime.MinValue

let chain f f2 a =
    match f a with
    | None -> f2 a
    | Some s -> Some s
let private (>>=) f1 f2 = chain f1 f2

let (|NotMandatory|MandatoryAndValid|MandatoryButEmpty|) a =
    match a with
    | (NullOrWhitespace, false) -> MandatoryAndValid
    | (NullOrWhitespace, true) -> MandatoryButEmpty
    | (_, _) -> NotMandatory

let (|ValidInt|NotAnInt|) a =
    match a with
    | str when (fst (Int32.TryParse(str))) -> ValidInt
    | _ -> NotAnInt

let (|ValidDecimal|NotADecimal|) a =
    match a with
    | str when isDecimal str -> ValidDecimal
    | _  -> NotADecimal
let (|ValidDate|NotADate|DateToBig|DateToSmall|) a =
    let str, o = a
    match DateTime.TryParse str with
    | (false, _) -> NotADate
    | (true, d) -> match d with
                   | d when d > (maxDateOrMax o.Max ) -> DateToBig
                   | d when d < (minDateOrMin o.Min) -> DateToSmall
                   | _ -> ValidDate

let (|ValidSelectOneValue|InvalidDropDownValue|) a =
    let str, o = a
    match o.Items |> List.tryFind (fun i -> i.Value = str) with
    | Some i -> ValidSelectOneValue
    | None -> InvalidDropDownValue

let validateField field =
    let validateMandatory field =
        match (field.Value, field.IsMandatory) with
        | MandatoryButEmpty -> Some (ValidationError.KeyOnly TranslationKey.FormValidation_MandatoryButEmpty)
        | NotMandatory
        | MandatoryAndValid -> None

    let validateType field =
        match field.FieldType with
        | FieldType.String -> None
        | FieldType.Int -> match field.Value with
                           | NotAnInt -> Some (ValidationError.KeyOnly TranslationKey.FormValidation_NotAnInt)
                           | ValidInt -> None
        | FieldType.Decimal -> match field.Value with
                               | NotADecimal -> Some (ValidationError.KeyOnly TranslationKey.FormValidation_NotADecimal)
                               | ValidDecimal -> None
        | FieldType.Date o -> match (field.Value, o) with
                              | ValidDate -> None
                              | NotADate -> Some (ValidationError.KeyOnly TranslationKey.FormValidation_NotADate)
                              | DateToBig -> Some (ValidationError.Init TranslationKey.FormValidation_DateToBig [|DayDateToGermanDate o.Max.Value|])
                              | DateToSmall -> Some (ValidationError.Init TranslationKey.FormValidation_DateToSmall [|DayDateToGermanDate o.Min.Value|] )
        | FieldType.SelectOne o -> match (field.Value, o) with
                                   | ValidSelectOneValue -> None
                                   | InvalidDropDownValue -> Some (ValidationError.KeyOnly TranslationKey.FormValidation_MandatoryButEmpty) //TODO: brauchen wir hier noch ne neue Meldung? Erreicht wird sie vermutlich nie

    let validate = validateMandatory >>= validateType
    match validate field with
    | Some str -> { field with Invalid = true; ValidationMessage = Some str }
    | None -> { field with Invalid = false; ValidationMessage = None }

let slider (isChecked : bool) (isDisabled : bool) (onChange : (Browser.Types.Event -> Unit) option) (text : string option) (title : string option) =
    label [] [ div [ ClassName "switch"
                     Title (match title with
                           | Some title -> title
                           | None -> "" ) ]
                   [ input [ Type "checkbox"
                             Disabled isDisabled
                             Checked isChecked
                             OnChange (match onChange with
                                         | Some onChange -> onChange
                                         | None -> (fun _ -> () )) ]
                     span [ ClassName "slider" ] [] ]
               (match text with
               | Some text -> span [ ClassName "ml-2" ] [ str text ]
               | None -> span [] [] ) ]

let spinner =
    div [ClassName "spinner" ] []

let card title content =
    div [ ClassName "card card-default" ]
        [ (match title with
           | Some title -> div [ ClassName "card-header" ]
                                [ div [ ClassName "header-block" ]
                                      [ p [ ClassName "title" ] [ title ] ] ]
           | None -> span [] [] )
          div [ ClassName "card-block" ] [ content ] ]

let toggleCard title content isOpen onToggle =
    div [ ClassName "card card-default" ]
        [ div [ ClassName "card-header" ]
              [ div [ ClassName "header-block" ]
                    [ a [ ClassName (match isOpen with
                                    | true -> "title toggle-open"
                                    | false -> "title toggle-closed")
                          Href "#"
                          OnClick onToggle ]
                        [ title ] ] ]
          (match isOpen with
          | true -> div [ ClassName "card-block" ] [ content ]
          | false -> span [] [] ) ]

let errorMessage s =
    [ span [ Class "has-error" ] [ str s] ]

type ModalAttributes<'a> = { Id: string
                             Title: string
                             Close: unit -> unit }

let modal { Id = id; Title = title; Close = close } content =
    div [ ClassName "modal fade show"
          Id id
          TabIndex -1
          Role "dialog"
          Style [ Display DisplayOptions.Block ] ]
        [ div [ ClassName "modal-dialog";
                Role "document"
                Style [ ZIndex "2000" ] ]
              [ div [ ClassName "modal-content"]
                    [ div [ ClassName "modal-header" ]
                          [ h4 [ ClassName "modal-title"; Id ( id + "Label")] [ str title ]
                            button [ ClassName "close"
                                     OnClick (fun _ -> close()) ]
                                   [ span [] [ str "x"] ] ]
                      div [ ClassName "modal-body" ]
                           content
                      div [ ClassName "modal-footer" ]
                          [ button [ ClassName "btn btn-primary"
                                     Type "button"
                                     OnClick (fun _ -> close()) ]
                                   [ str CurrentTranslations.Translations.[TranslationKey.Modal_Close_Button_Text]]]]]
          div [ Class "modal-backdrop fade show" ] [] ]